import React, { useState, useEffect, useRef, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useAppContext } from '../../AppContext';
import LoadingSpinner from '../LoadingSpinner';
import * as LucideIcons from 'lucide-react';
import { updatePlayerSurvival } from '../../services/challengeService';

// Lazy load games
const AuraEater = React.lazy(() => import('../DreamWeave/games/aura-eater/AuraEater'));
const SynapticStacking = React.lazy(() => import('../DreamWeave/SynapticStacking'));
const SynapticSerpent = React.lazy(() => import('../DreamWeave/games/synaptic-serpent/SynapticSerpent'));
const CosmicAscent = React.lazy(() => import('../DreamWeave/CosmicAscent'));
const StarlightSentinel = React.lazy(() => import('../DreamWeave/games/starlight-sentinel/StarlightSentinel'));
const HarmonicResonance = React.lazy(() => import('../DreamWeave/games/harmonic-resonance/HarmonicResonance'));
const NexusDreamWeaveShowdown = React.lazy(() => import('../NexusDreamWeaveShowdown'));

const ShowdownArena = ({ challengeId, onExit }) => {
    const { db, user, currentUserProfile } = useAppContext();
    const [challengeData, setChallengeData] = useState(null);
    const [opponentProfile, setOpponentProfile] = useState(null);
    const [gameType, setGameType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [playerFinished, setPlayerFinished] = useState(false);
    
    const gameStartTimeRef = useRef(null);

    // Subscribe to Challenge Updates
    useEffect(() => {
        if (!challengeId || !db) return;

        const challengeRef = doc(db, 'artifacts/whispers-of-harmony/public/data/challenges', challengeId);
        const unsubscribe = onSnapshot(challengeRef, async (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setChallengeData(data);
                setGameType(data.gameType || 'auraEater');
                setLoading(false);

                // Fetch Opponent Profile
                const opponentId = data.initiatorId === user?.uid ? data.targetId : data.initiatorId;
                if (opponentId && opponentId !== 'ai_consciousness') {
                    const oppRef = doc(db, `artifacts/whispers-of-harmony/public/data/user_profiles/${opponentId}`);
                    const oppDoc = await getDoc(oppRef);
                    if (oppDoc.exists()) {
                        setOpponentProfile({ id: opponentId, ...oppDoc.data() });
                    }
                } else if (opponentId === 'ai_consciousness') {
                    setOpponentProfile({
                        id: 'ai_consciousness',
                        displayName: 'AI Consciousness',
                        photoURL: 'https://placehold.co/40x40/9333EA/FFFFFF?text=AI'
                    });
                }
            } else {
                setLoading(false);
            }
        }, (err) => {
            console.error("Firestore listener error:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [challengeId, db, user]);

    // Record start time when the game is rendered
    useEffect(() => {
        if (!loading && !gameStartTimeRef.current) {
            gameStartTimeRef.current = Date.now();
        }
    }, [loading]);

    const handleGameSubmit = async (gameId, score, gameLog) => {
        if (!challengeId || !challengeData || !user) return;

        const role = challengeData.initiatorId === user.uid ? 'initiator' : 'target';
        const survivalTime = Math.max(1, Math.floor((Date.now() - gameStartTimeRef.current) / 1000));
        
        setPlayerFinished(true);

        try {
            // Update current user's results
            await updatePlayerSurvival(challengeId, user.uid, role, survivalTime, score, true);

            // If opponent is AI, automatically submit simulated results for the AI
            if (challengeData.targetId === 'ai_consciousness') {
                const aiScore = Math.max(0, Math.floor(score * (0.6 + Math.random() * 0.8)));
                const aiSurvival = Math.max(1, Math.floor(survivalTime * (0.7 + Math.random() * 0.6)));
                await updatePlayerSurvival(challengeId, 'ai_consciousness', 'target', aiSurvival, aiScore, true);
            }
        } catch (error) {
            console.error("Error submitting game over stats:", error);
        }
    };

    const handleGameOver = () => {
        // Fallback in case the game doesn't call onGameSubmit but calls onGameOver
        if (!playerFinished) {
            handleGameSubmit(gameType, 0, []);
        }
    };

    if (loading) return <LoadingSpinner message="Preparing Arena..." />;

    const role = challengeData?.initiatorId === user?.uid ? 'initiator' : 'target';
    const opponentName = opponentProfile?.displayName || 'Opponent';

    const isMatchCompleted = challengeData?.status === 'completed';
    const isWinner = challengeData?.winnerId === user?.uid;
    const winnerName = challengeData?.winnerId === user?.uid ? 'You' : (challengeData?.winnerId === 'ai_consciousness' ? 'AI Consciousness' : opponentName);

    const initiatorResult = challengeData?.results?.[challengeData?.initiatorId];
    const targetResult = challengeData?.results?.[challengeData?.targetId];

    const myResult = role === 'initiator' ? initiatorResult : targetResult;
    const rivalResult = role === 'initiator' ? targetResult : initiatorResult;

    // RENDER MAIN GAMEPLAY OR WAITING SCREEN
    const renderContent = () => {
        if (isMatchCompleted) {
            return (
                <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-xl z-50 flex flex-col items-center justify-center p-6 text-center select-none">
                    <motion.div 
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="max-w-md w-full bg-slate-900 border border-cyan-500/20 p-8 rounded-3xl shadow-2xl relative overflow-hidden"
                    >
                        {/* Title */}
                        <div className="mb-6">
                            <LucideIcons.Trophy className={`w-20 h-20 mx-auto mb-4 ${isWinner ? 'text-yellow-400' : 'text-gray-600'}`} />
                            <h2 className="text-3xl font-black font-orbitron tracking-wider text-white">
                                {isWinner ? "VICTORY" : "DEFEAT"}
                            </h2>
                            <p className="text-sm text-cyan-400 font-medium mt-1">
                                {challengeData.victoryReason || "Game concluded"}
                            </p>
                        </div>

                        {/* Scores comparisons */}
                        <div className="bg-slate-950/80 p-5 rounded-2xl border border-white/5 flex flex-col gap-3 text-sm mb-6">
                            <div className="flex justify-between items-center text-xs text-gray-500 border-b border-white/5 pb-2">
                                <span>Player</span>
                                <span>Survival Time</span>
                                <span>Score</span>
                            </div>
                            <div className="flex justify-between items-center font-medium">
                                <span className="text-white">You</span>
                                <span className="text-cyan-300">{myResult?.survivalTime || 0}s</span>
                                <span className="text-cyan-300 font-bold">{myResult?.score || 0}</span>
                            </div>
                            <div className="flex justify-between items-center font-medium">
                                <span className="text-gray-400">{opponentName}</span>
                                <span className="text-pink-300">{rivalResult?.survivalTime || 0}s</span>
                                <span className="text-pink-300 font-bold">{rivalResult?.score || 0}</span>
                            </div>
                        </div>

                        {/* Reward info */}
                        <div className="mb-8">
                            <span className="text-xs text-gray-400 uppercase tracking-widest block">Souls Balance</span>
                            <span className={`text-2xl font-black font-orbitron ${isWinner ? 'text-green-400' : 'text-red-400'}`}>
                                {isWinner ? "+50 Souls" : "-50 Souls"}
                            </span>
                        </div>

                        <button 
                            onClick={onExit}
                            className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full font-bold text-white hover:scale-105 transition-transform"
                        >
                            Return to Lobby
                        </button>
                    </motion.div>
                </div>
            );
        }

        if (playerFinished) {
            return (
                <div className="absolute inset-0 bg-slate-950/90 z-40 flex flex-col items-center justify-center p-6 text-center select-none">
                    <LucideIcons.Loader2 className="w-12 h-12 text-cyan-400 animate-spin mb-4" />
                    <h3 className="text-xl font-bold font-orbitron text-white">Your run is locked.</h3>
                    <p className="text-sm text-gray-400 mt-2 max-w-xs">
                        Waiting for {opponentName} to complete their challenge session...
                    </p>
                    <div className="bg-slate-900/50 p-4 rounded-xl border border-white/5 mt-6 w-64 text-left text-xs text-gray-400 flex flex-col gap-2">
                        <div className="flex justify-between">
                            <span>Your Score:</span>
                            <span className="text-cyan-300 font-bold">{myResult?.score || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Survival Time:</span>
                            <span className="text-cyan-300 font-bold">{myResult?.survivalTime || 0}s</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Render target minigame
        const nexusId = challengeData?.nexusId || currentUserProfile?.primaryNexusId || 'global_arena';
        const mySouls = currentUserProfile?.nexusStats?.[nexusId]?.souls || currentUserProfile?.souls || 0;

        const commonProps = {
            challengeId: challengeId,
            nexusId: nexusId,
            currentUser: currentUserProfile,
            rivalId: opponentProfile?.id,
            souls: mySouls,
            onClose: onExit, 
            onGameSubmit: handleGameSubmit,
            onGameOver: handleGameOver,
            isSpectatorMode: false,
            survivalMode: true,
            autoStart: true 
        };

        switch (gameType) {
            case 'celestial': return <Suspense fallback={<LoadingSpinner />}><NexusDreamWeaveShowdown {...commonProps} /></Suspense>;
            case 'auraEater': return <Suspense fallback={<LoadingSpinner />}><AuraEater {...commonProps} /></Suspense>;
            case 'starlightSentinel': return <Suspense fallback={<LoadingSpinner />}><StarlightSentinel {...commonProps} /></Suspense>;
            case 'synapticSerpent': return <Suspense fallback={<LoadingSpinner />}><SynapticSerpent {...commonProps} /></Suspense>;
            case 'synapticStacking': return <Suspense fallback={<LoadingSpinner />}><SynapticStacking {...commonProps} /></Suspense>;
            case 'cosmicAscent': return <Suspense fallback={<LoadingSpinner />}><CosmicAscent {...commonProps} /></Suspense>;
            default: return (
                <div className="flex flex-col items-center justify-center h-full text-red-500 gap-4">
                    <p className="font-bold">Unknown Game Type: {gameType}</p>
                    <button onClick={onExit} className="px-6 py-2 bg-gray-800 rounded text-white">Exit</button>
                </div>
            );
        }
    };

    return (
        <div className="w-full h-full bg-black relative flex flex-col">
            {renderContent()}
        </div>
    );
};

export default ShowdownArena;